<template>
  <div v-if="open" class="references-modal-overlay" @click.self="handleClose">
    <div class="references-modal" role="dialog" aria-modal="true" aria-labelledby="references-modal-title">
      <div class="references-modal__header">
        <h2 id="references-modal-title">行銷引用清單</h2>
        <button type="button" class="references-modal__close" aria-label="關閉" @click="handleClose">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="references-modal__body">
        <div v-if="loading" class="references-modal__loading">
          <div class="spinner"></div>
          <p>載入中...</p>
        </div>

        <div v-else-if="error" class="references-modal__error">
          <p>{{ error }}</p>
          <button type="button" class="btn btn-secondary btn-sm" @click="fetchReferences">重試</button>
        </div>

        <div v-else-if="references.length === 0" class="references-modal__empty">
          <i class="ri-links-line"></i>
          <p>目前沒有其他行銷流程或工具引用此模板</p>
        </div>

        <template v-else>
          <div class="references-summary">
            共 {{ totalCount }} 處引用
            <span v-if="activeCount > 0" class="references-summary__active">
              （{{ activeCount }} 處進行中，停用後將無法自動發券）
            </span>
          </div>

          <div class="references-list">
            <div
              v-for="(item, index) in references"
              :key="`${item.sourceType}-${item.sourceId}-${index}`"
              class="reference-item"
            >
              <div class="reference-item__main">
                <div class="reference-item__title-row">
                  <span class="reference-item__type">{{ item.sourceTypeLabel }}</span>
                  <span :class="['reference-item__status', item.isActive ? 'is-active' : 'is-inactive']">
                    {{ item.statusLabel }}
                  </span>
                </div>
                <div class="reference-item__name">{{ item.sourceName }}</div>
                <div v-if="item.contextLabel" class="reference-item__context">{{ item.contextLabel }}</div>
              </div>
              <router-link
                v-if="item.detailPath"
                :to="item.detailPath"
                class="btn btn-sm btn-secondary reference-item__link"
                @click="handleClose"
              >
                查看
              </router-link>
            </div>
          </div>
        </template>
      </div>

      <div class="references-modal__footer">
        <button type="button" class="btn btn-secondary" @click="handleClose">關閉</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { couponService } from '@/api/modules/coupons.js';

export default {
  name: 'CouponTemplateReferencesModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    templateId: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const loading = ref(false);
    const error = ref(null);
    const references = ref([]);
    const activeCount = ref(0);
    const totalCount = ref(0);

    const fetchReferences = async () => {
      if (!props.templateId) return;

      loading.value = true;
      error.value = null;

      try {
        const response = await couponService.getTemplateReferences(props.templateId);
        if (response?.success && response.data) {
          references.value = response.data.references || [];
          activeCount.value = response.data.activeCount || 0;
          totalCount.value = response.data.totalCount || 0;
        } else {
          error.value = response?.message || '查詢引用失敗';
        }
      } catch (err) {
        console.error('查詢優惠券模板引用失敗:', err);
        error.value = err.message || '查詢引用失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleClose = () => {
      emit('close');
    };

    watch(
      () => [props.open, props.templateId],
      ([isOpen, templateId]) => {
        if (isOpen && templateId) {
          fetchReferences();
        }
      },
      { immediate: true }
    );

    return {
      loading,
      error,
      references,
      activeCount,
      totalCount,
      fetchReferences,
      handleClose
    };
  }
};
</script>

<style scoped>
.references-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}

.references-modal {
  width: 100%;
  max-width: 640px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
}

.references-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.references-modal__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.references-modal__close {
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 22px;
  cursor: pointer;
  line-height: 1;
}

.references-modal__body {
  padding: 16px 20px;
  overflow-y: auto;
}

.references-modal__loading,
.references-modal__empty,
.references-modal__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 180px;
  color: #6b7280;
  text-align: center;
}

.references-modal__empty i {
  font-size: 36px;
  color: #d1d5db;
}

.references-summary {
  margin-bottom: 12px;
  font-size: 13px;
  color: #4b5563;
}

.references-summary__active {
  color: #b45309;
}

.references-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.reference-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.reference-item__main {
  flex: 1;
  min-width: 0;
}

.reference-item__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.reference-item__type {
  font-size: 12px;
  font-weight: 600;
  color: #4f46e5;
}

.reference-item__status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
}

.reference-item__status.is-active {
  background: #dcfce7;
  color: #166534;
}

.reference-item__status.is-inactive {
  background: #f3f4f6;
  color: #6b7280;
}

.reference-item__name {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.reference-item__context {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}

.reference-item__link {
  flex-shrink: 0;
  white-space: nowrap;
}

.references-modal__footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid #e5e7eb;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
